import styled, { css } from 'styled-components';
import { ThemeColors } from '../theme';
import { ReactNode } from 'react';
import { TextCSS } from './Text';
import { SizesValues } from '../type';

const ButtonWrapper = styled.button<{
  $color: ThemeColors;
  $flex?: boolean | undefined;
}>`
  display: flex;
  flex-direction: row;
  background: ${(props) => props.theme.color[props.$color]};
  border: ${(props) => `0.3${props.theme.unit}`} solid #000;
  box-shadow: 0 ${(props) => `0.5${props.theme.unit}`} rgba(0, 0, 0, 0.3);
  border-radius: ${(props) => `0.7${props.theme.unit}`};
  padding: 0;
  transform: skew(-5deg);
  overflow: hidden;
  cursor: pointer;
  ${(props) => props.$flex && `flex:1;`}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  &:hover:not(:disabled) {
    background: color-mix(
      in srgb,
      ${(props) => props.theme.color[props.$color]},
      black 20%
    );
  }
`;
export const ButtonStyle = ({ $size = 'M' }: { $size: SizesValues }) => css`
  ${TextCSS({ size: $size })}
  color: ${(props) => props.theme.color.white};
  padding: ${(props) =>
    `${$size === 'L' ? '0' : '1'}${props.theme.unit} ${$size === 'L' ? '2' : '3'}${props.theme.unit}`};
  line-height: 1;
`;

const ButtonElement = styled.div<{
  $color: ThemeColors;
  $size: SizesValues;
}>`
  ${(props) => ButtonStyle({ $size: props.$size })}

  position: relative;
  width: 100%;
  border: 0;
  border-style: solid;
  border-width: ${(props) => `0.5${props.theme.unit}`} 0;

  border-top-color: color-mix(
    in srgb,
    ${(props) => props.theme.color[props.$color]},
    white 25%
  );
  border-bottom-color: color-mix(
    in srgb,
    ${(props) => props.theme.color[props.$color]},
    black 20%
  );

  &:after {
    content: ' ';
    display: block;
    position: absolute;
    top: ${(props) => `-0.5${props.theme.unit}`};
    right: 0;
    height: ${(props) => `1${props.theme.unit}`};
    width: ${(props) => `1${props.theme.unit}`};
    background: red;
    clip-path: polygon(0 0, 100% 100%, 100% 0);
    background: color-mix(
      in srgb,
      ${(props) => props.theme.color[props.$color]},
      white 35%
    );
  }

  &:hover:not(:disabled) {
    background: color-mix(
      in srgb,
      ${(props) => props.theme.color[props.$color]},
      black 20%
    );
    border-top-color: color-mix(
      in srgb,
      ${(props) => props.theme.color[props.$color]},
      black 10%
    );
    border-bottom-color: color-mix(
      in srgb,
      ${(props) => props.theme.color[props.$color]},
      black 40%
    );
    &:after {
      background: color-mix(
        in srgb,
        ${(props) => props.theme.color[props.$color]},
        white 10%
      );
    }
  }
`;

const ButtonChildren = styled.div`
  transform: skew(5deg);
`;

export type ButtonProps = {
  type?: 'button' | 'submit';
  color: ThemeColors;
  children: ReactNode;
  disabled?: boolean | undefined;
  flex?: boolean | undefined;
  size?: SizesValues;
  onClick?: (() => void) | undefined;
};

export const Button = ({
  type = 'button',
  color,
  children,
  disabled,
  flex,
  onClick,
  size = 'M',
}: ButtonProps) => {
  return (
    <ButtonWrapper
      type={type}
      onClick={onClick}
      $color={color}
      disabled={disabled}
      $flex={flex}
    >
      <ButtonElement $color={color} $size={size}>
        <ButtonChildren>{children}</ButtonChildren>
      </ButtonElement>
    </ButtonWrapper>
  );
};
